import React, { createRef } from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  Zoom,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PhoneIcon from '@material-ui/icons/Phone';

/**
 * DuplicateCustomerPanel
 *
 * Props:
 *   matches     – array of customer objects (same shape as blacklist matches)
 *   searchLabel – short description of what was searched, e.g. "John Smith" or "555-1234"
 *   matchType   – 'name' | 'phone'   (drives the header wording)
 *   onDismiss   – called when the user clicks ✕
 */
class DuplicateCustomerPanel extends React.Component {
  constructor(props) {
    super(props);
    this.sliderRef = createRef();
    this.state = {
      expandedId: null,
      zoomedPhoto: null,
      canScrollLeft: false,
      canScrollRight: false,
    };
    this.isDragging = false;
    this.dragStartX = 0;
    this.dragStartScroll = 0;
  }

  componentDidMount() {
    this.checkScrollButtons();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.matches !== this.props.matches) {
      setTimeout(this.checkScrollButtons, 50);
    }
  }

  checkScrollButtons = () => {
    const el = this.sliderRef.current;
    if (!el) return;
    this.setState({
      canScrollLeft: el.scrollLeft > 4,
      canScrollRight: el.scrollLeft < el.scrollWidth - el.clientWidth - 4,
    });
  };

  scrollBy = (direction) => {
    const el = this.sliderRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 200, behavior: 'smooth' });
    setTimeout(this.checkScrollButtons, 300);
  };

  onMouseDown = (e) => {
    this.isDragging = true;
    this.dragStartX = e.pageX - this.sliderRef.current.offsetLeft;
    this.dragStartScroll = this.sliderRef.current.scrollLeft;
    this.sliderRef.current.style.cursor = 'grabbing';
    this.sliderRef.current.style.userSelect = 'none';
  };

  onMouseMove = (e) => {
    if (!this.isDragging) return;
    const x = e.pageX - this.sliderRef.current.offsetLeft;
    this.sliderRef.current.scrollLeft =
      this.dragStartScroll - (x - this.dragStartX);
    this.checkScrollButtons();
  };

  onMouseUp = () => {
    this.isDragging = false;
    if (this.sliderRef.current) {
      this.sliderRef.current.style.cursor = 'grab';
      this.sliderRef.current.style.userSelect = '';
    }
  };

  toggleExpand = (customerId) => {
    this.setState((prev) => ({
      expandedId: prev.expandedId === customerId ? null : customerId,
    }));
  };

  render() {
    const { matches, searchLabel, matchType = 'name', onDismiss } = this.props;
    const { expandedId, zoomedPhoto, canScrollLeft, canScrollRight } =
      this.state;

    if (!matches || matches.length === 0) return null;

    const showArrows = matches.length > 3;

    const isPhone = matchType === 'phone';

    // Colour palette — amber/orange to distinguish from the red blacklist panel
    const palette = {
      border: '#e65100',
      header: '#bf360c',
      subBg: '#fff8f5',
      subBorder: '#ffccbc',
      cardBorder: '#ffccbc',
      cardBorderExpanded: '#e65100',
      cardBg: 'white',
      cardBgExpanded: '#fff3e0',
      avatarRing: '#e65100',
      badgeBg: '#e65100',
      reasonBg: '#bf360c',
      nameColor: '#7f2800',
      tapHint: '#e65100',
      shadow: 'rgba(230,81,0,0.18)',
      chip: 'rgba(255,255,255,0.22)',
      scrollBg: '#fff8f5',
      gradientL: '#fff8f5',
      gradientR: '#fff8f5',
    };

    const headerLabel = isPhone
      ? `DUPLICATE PHONE — "${searchLabel}"`
      : `DUPLICATE NAME — "${searchLabel}"`;

    const subLabel = isPhone
      ? 'Another customer already has this phone number. Confirm this is a different person.'
      : 'These customers share the same name. Please verify this is not an existing customer.';

    return (
      <>
        <Box
          sx={{
            mx: 2,
            mt: 0,
            mb: 2,
            borderRadius: '12px',
            overflow: 'hidden',
            border: `2px solid ${palette.border}`,
            boxShadow: `0 4px 24px ${palette.shadow}`,
            animation: 'slideDown 0.25s ease',
            '@keyframes slideDown': {
              from: { opacity: 0, transform: 'translateY(-8px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {/* ── Header ── */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: palette.header,
              px: 2.5,
              py: 1.2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <InfoOutlinedIcon sx={{ color: '#ffe0b2', fontSize: 22 }} />
              <Typography
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: '0.9rem',
                  letterSpacing: 0.5,
                }}
              >
                {headerLabel}
              </Typography>
              <Chip
                label={`${matches.length} found`}
                size="small"
                sx={{
                  bgcolor: palette.chip,
                  color: 'white',
                  fontWeight: 700,
                  fontSize: '0.7rem',
                  height: 20,
                }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={onDismiss}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                '&:hover': { color: 'white' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* ── Sub-heading ── */}
          <Box
            sx={{
              bgcolor: palette.subBg,
              px: 2.5,
              py: 1,
              borderBottom: `1px solid ${palette.subBorder}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              sx={{ color: '#bf360c', fontSize: '0.78rem', fontWeight: 600 }}
            >
              {subLabel}
            </Typography>
            {showArrows && (
              <Typography
                sx={{
                  color: '#aaa',
                  fontSize: '0.7rem',
                  ml: 2,
                  whiteSpace: 'nowrap',
                }}
              >
                drag or use arrows to scroll
              </Typography>
            )}
          </Box>

          {/* ── Slider wrapper ── */}
          <Box sx={{ position: 'relative', bgcolor: palette.scrollBg }}>
            {/* Left arrow */}
            {showArrows && (
              <IconButton
                onClick={() => this.scrollBy(-1)}
                disabled={!canScrollLeft}
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: canScrollLeft ? 'white' : 'transparent',
                  boxShadow: canScrollLeft
                    ? '2px 0 8px rgba(0,0,0,0.12)'
                    : 'none',
                  width: 32,
                  height: 32,
                  opacity: canScrollLeft ? 1 : 0.25,
                  borderRadius: '0 8px 8px 0',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ChevronLeftIcon sx={{ fontSize: 20, color: palette.border }} />
              </IconButton>
            )}

            {/* Right arrow */}
            {showArrows && (
              <IconButton
                onClick={() => this.scrollBy(1)}
                disabled={!canScrollRight}
                sx={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 2,
                  bgcolor: canScrollRight ? 'white' : 'transparent',
                  boxShadow: canScrollRight
                    ? '-2px 0 8px rgba(0,0,0,0.12)'
                    : 'none',
                  width: 32,
                  height: 32,
                  opacity: canScrollRight ? 1 : 0.25,
                  borderRadius: '8px 0 0 8px',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                <ChevronRightIcon
                  sx={{ fontSize: 20, color: palette.border }}
                />
              </IconButton>
            )}

            {/* Fade gradients */}
            {showArrows && canScrollLeft && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 32,
                  top: 0,
                  bottom: 0,
                  width: 32,
                  zIndex: 1,
                  background: `linear-gradient(to right, ${palette.gradientL}, transparent)`,
                  pointerEvents: 'none',
                }}
              />
            )}
            {showArrows && canScrollRight && (
              <Box
                sx={{
                  position: 'absolute',
                  right: 32,
                  top: 0,
                  bottom: 0,
                  width: 32,
                  zIndex: 1,
                  background: `linear-gradient(to left, ${palette.gradientR}, transparent)`,
                  pointerEvents: 'none',
                }}
              />
            )}

            {/* ── Card row ── */}
            <Box
              ref={this.sliderRef}
              onScroll={this.checkScrollButtons}
              onMouseDown={this.onMouseDown}
              onMouseMove={this.onMouseMove}
              onMouseUp={this.onMouseUp}
              onMouseLeave={this.onMouseUp}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                overflowX: 'auto',
                overflowY: 'visible',
                gap: 2,
                p: 2,
                px: showArrows ? 4 : 2,
                cursor: 'grab',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {matches.map((customer) => {
                const isExpanded = expandedId === customer.customerId;
                const photo =
                  customer.files?.[0]?.localURL || customer.capturedImageURL;

                return (
                  <Box
                    key={customer.customerId}
                    onClick={() => this.toggleExpand(customer.customerId)}
                    sx={{
                      flex: '0 0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                      borderRadius: '12px',
                      border: isExpanded
                        ? `2px solid ${palette.cardBorderExpanded}`
                        : `2px solid ${palette.cardBorder}`,
                      bgcolor: isExpanded
                        ? palette.cardBgExpanded
                        : palette.cardBg,
                      p: 1.5,
                      width: 160,
                      transition: 'border 0.2s ease, box-shadow 0.2s ease',
                      '&:hover': {
                        border: `2px solid ${palette.cardBorderExpanded}`,
                        boxShadow: `0 4px 12px ${palette.shadow}`,
                      },
                    }}
                  >
                    {/* Photo */}
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        src={photo}
                        variant="rounded"
                        sx={{
                          width: 120,
                          height: 140,
                          borderRadius: '8px',
                          boxShadow: isExpanded
                            ? `0 0 0 3px ${palette.avatarRing}`
                            : '0 2px 8px rgba(0,0,0,0.12)',
                        }}
                      />

                      {/* Zoom — bottom-right */}
                      {photo && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.setState({ zoomedPhoto: photo });
                          }}
                          sx={{
                            position: 'absolute',
                            bottom: -10,
                            right: -10,
                            bgcolor: '#1e2b58',
                            border: '2px solid white',
                            width: 28,
                            height: 28,
                            boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                            '&:hover': { bgcolor: '#151d3d' },
                          }}
                        >
                          <ZoomInIcon sx={{ color: 'white', fontSize: 15 }} />
                        </IconButton>
                      )}
                    </Box>

                    {/* Name */}
                    <Typography
                      sx={{
                        mt: 1.8,
                        fontWeight: 800,
                        fontSize: '0.8rem',
                        color: palette.nameColor,
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        lineHeight: 1.2,
                        width: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {customer.firstName} {customer.lastName}
                    </Typography>

                    {/* Phone */}
                    {customer.phone && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.4,
                          mt: 0.4,
                        }}
                      >
                        <PhoneIcon
                          style={{ width: 16, height: 16, color: '#888' }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.73rem',
                            color: '#555',
                            fontWeight: 600,
                          }}
                        >
                          {customer.phone}
                        </Typography>
                      </Box>
                    )}

                    {/* Tap hint */}
                    <Typography
                      sx={{
                        fontSize: '0.63rem',
                        color: palette.tapHint,
                        mt: 0.5,
                        fontWeight: 600,
                        opacity: isExpanded ? 0 : 0.7,
                      }}
                    >
                      {isExpanded ? '' : 'Tap for details'}
                    </Typography>

                    {/* Expanded detail */}
                    <Collapse in={isExpanded} timeout={200}>
                      <Box
                        sx={{
                          mt: 1,
                          bgcolor: palette.reasonBg,
                          borderRadius: '8px',
                          px: 1.5,
                          py: 1,
                          width: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            color: '#ffccbc',
                            fontWeight: 700,
                            letterSpacing: 1,
                            mb: 0.3,
                          }}
                        >
                          EXISTING CUSTOMER
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.76rem',
                            color: 'white',
                            fontStyle: 'italic',
                            lineHeight: 1.4,
                          }}
                        >
                          {customer.comments
                            ? customer.comments
                            : isPhone
                            ? `Phone already registered to this customer.`
                            : `Name matches an existing customer record.`}
                        </Typography>
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* ── Zoom dialog ── */}
        <Dialog
          open={!!zoomedPhoto}
          onClose={() => this.setState({ zoomedPhoto: null })}
          maxWidth="md"
          TransitionComponent={Zoom}
          PaperProps={{
            sx: {
              bgcolor: '#000',
              boxShadow: 'none',
              borderRadius: '12px',
              overflow: 'hidden',
            },
          }}
        >
          <Box
            sx={{ position: 'relative', cursor: 'zoom-out' }}
            onClick={() => this.setState({ zoomedPhoto: null })}
          >
            <IconButton
              onClick={() => this.setState({ zoomedPhoto: null })}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                zIndex: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.85)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={zoomedPhoto}
              alt="Enlarged"
              style={{
                display: 'block',
                maxWidth: '90vw',
                maxHeight: '85vh',
                objectFit: 'contain',
              }}
            />
            <Typography
              sx={{
                textAlign: 'center',
                color: 'rgba(255,255,255,0.4)',
                fontSize: '0.75rem',
                py: 1,
              }}
            >
              Click anywhere to close
            </Typography>
          </Box>
        </Dialog>
      </>
    );
  }
}

export default DuplicateCustomerPanel;
